import React, { useState } from 'react'
import ArcadeEmbed from '../ArcadeEmbed'
import {
	PlayIcon,
	SparklesIcon,
	FunnelIcon,
	ShieldCheckIcon,
	CurrencyDollarIcon,
	MapIcon,
	EnvelopeIcon,
	ChatBubbleBottomCenterTextIcon,
	AdjustmentsHorizontalIcon,
	BellAlertIcon,
} from '@heroicons/react/24/solid'
import arcadeImage from '../../media/arcade_software.png'

import './glowEffect.css'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const Hero = ({ chromeWebstoreUrl }) => {
	const [showDemo, setShowDemo] = useState(false)
	const theme = useGlobalTheme()
	const isLight = theme === 'light'
	return (
		<div className={`pt-6 pb-0 flex items-start relative ${isLight ? 'bg-white text-gray-900' : 'bg-gradient-to-b from-[#001b2e] to-[#003456] text-[#eef3f9]'}`}>
			<div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 w-full hero-container">
				<div className="px-6 sm:px-7 lg:px-7 hero-inner-wrapper">
					<div className={`rounded-t-3xl py-8 px-12 mx-auto max-w-7xl mt-2 pb-32 hero-content-box ${isLight ? 'bg-sky-50 border border-sky-100' : 'bg-[#002340]'}`}>
						<div className="text-center">
							<div className="inline-flex items-center justify-center mb-8">
								<span className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-sm font-bold px-6 py-2 rounded-full uppercase tracking-wider shadow-lg flex items-center gap-2 hero-badge">
									<SparklesIcon className="w-4 h-4" />
									AI-powered feature-rich extension for DAT
								</span>
							</div>
							<h1 className={`text-6xl font-extrabold mb-4 leading-tight tracking-tight hero-heading ${isLight ? 'text-gray-900' : 'text-white'}`}>
								Book
								<span className={`px-5 py-3 mx-2 rounded-2xl transform -rotate-1 inline-block hero-heading-highlight ${isLight ? 'bg-sky-100 text-sky-900' : 'bg-[#003d5c] text-white'}`}>
									Better Loads
								</span>
								<br />
								Before Anyone Else
							</h1>

							<div className={`text-base mb-8 max-w-4xl mx-auto hero-features-container ${isLight ? 'text-gray-700' : 'text-[#b3cde4]'}`}>
								<div className="flex flex-wrap justify-center gap-x-5 gap-y-2 hero-features-grid">
									<span className="flex items-center gap-1.5 hero-feature-item">
										<FunnelIcon className="w-4 h-4 text-blue-400" />
										AI-powered filters
									</span>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<ShieldCheckIcon className="w-4 h-4 text-blue-400" />
										AI risk scores
									</span>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<CurrencyDollarIcon className="w-4 h-4 text-blue-400" />
										Factoring Score
									</span>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<MapIcon className="w-4 h-4 text-blue-400" />
										Built-in Google Maps
									</span>
									<div className="w-full hero-features-divider"></div>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<EnvelopeIcon className="w-4 h-4 text-blue-400" />
										Click-to-email
									</span>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<ChatBubbleBottomCenterTextIcon className="w-4 h-4 text-blue-400" />
										AI Email replies
									</span>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<AdjustmentsHorizontalIcon className="w-4 h-4 text-blue-400" />
										Tab-specific filters
									</span>
									<span className="flex items-center gap-1.5 hero-feature-item">
										<BellAlertIcon className="w-4 h-4 text-blue-400" />
										Load-match alerts
									</span>
								</div>
							</div>

							<div className="flex flex-col items-center">
								<a
									href={chromeWebstoreUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-20 py-4 rounded-full text-lg font-bold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-2xl hover:shadow-3xl transform hover:scale-105 tracking-wider hero-cta-button"
								>
									Start 7-Day Trial
								</a>
								<p className={`text-sm mt-3 text-center ${isLight ? 'text-gray-600' : 'text-[#b3cde4]'}`}>
									No credit card required
								</p>

								<div className="mt-6 w-full -mb-24 relative hero-demo-wrapper">
									<div className="absolute -top-12 right-8 z-30 flex items-start gap-2 hero-demo-hint">
										<svg
											className="w-9 h-9 text-blue-400 scale-x-[-1]"
											viewBox="0 0 302.816 302.816"
											fill="currentColor"
										>
											<path d="M298.423,152.996c-5.857-5.858-15.354-5.858-21.213,0l-35.137,35.136 c-5.871-59.78-50.15-111.403-112.001-123.706c-45.526-9.055-92.479,5.005-125.596,37.612c-5.903,5.813-5.977,15.31-0.165,21.213 c5.813,5.903,15.31,5.977,21.212,0.164c26.029-25.628,62.923-36.679,98.695-29.565c48.865,9.72,83.772,50.677,88.07,97.978 l-38.835-38.835c-5.857-5.857-15.355-5.858-21.213,0.001c-5.858,5.858-5.858,15.355,0,21.213l62.485,62.485 c2.929,2.929,6.768,4.393,10.606,4.393s7.678-1.464,10.607-4.393l62.483-62.482C304.281,168.352,304.281,158.854,298.423,152.996z" />
										</svg>

										<span className={`text-base font-normal rotate-6 hero-demo-hint-text ${isLight ? 'text-blue-600' : 'text-blue-400'}`}>
											Play with this demo
										</span>
									</div>

									{!showDemo ? (
										<div className="relative rounded-t-3xl shadow-2xl overflow-hidden">
											<img
												src={arcadeImage}
												alt="LoadLine Demo Preview"
												className="w-full h-full object-cover opacity-20"
											/>
											<div className="absolute inset-0 flex items-center justify-center">
												<button
													onClick={() => setShowDemo(true)}
													className="bg-white text-gray-900 px-12 py-6 rounded-full text-xl font-bold hover:scale-105 transition-all duration-200 shadow-xl flex items-center gap-4 group hero-demo-button"
												>
													<PlayIcon className="w-8 h-8 text-purple-600 group-hover:text-purple-700" />
													See how it works
												</button>
											</div>
										</div>
									) : (
										<ArcadeEmbed
											id="0qGd5OORhB2oDfOO6VgU" // https://app.arcade.software/share/0qGd5OORhB2oDfOO6VgU
											title="Search Loads - DAT One"
											onEvent={data => console.log('Arcade event', data)}
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Hero
